/**
 * build-child-payload.ts — pure function for building a child report payload
 * from a local parent payload (D3 offline inheritance engine, REQ-5).
 *
 * This function is the ONLY code path for all inheritance transitions.
 * It works offline because it reads from the in-memory parent payload.
 *
 * Transitions:
 *   IE → IS: copies datosGenerales, equipos ONLY.
 *            Starts with a fresh 7-slot default panelFotografico and empty actividades.
 *            Actividades auto-fill from the IS's OWN panel via the seeder in InformeEditor.
 *            conclusionesRecomendaciones seeded with the IS's 4 fixed defaults (not inherited).
 *   IE → AC: copies datosGenerales, equipos ONLY. actividades and conclusiones start empty.
 *   IS → AC: copies datosGenerales, equipos, actividades, conclusionesRecomendaciones.
 *
 * // ponytail: D3 = wiring existing seams. Ceiling = if more transition types
 * // are added, extend the parentTipo/childTipo union and add a new branch here.
 */

import type { AcPayload, IeFoto, IePayload, IsActividad, IsPayload, TipoInforme } from "../types.js";
import { PANEL_DEFAULT_TITULOS } from "./ie/ie-payload.js";
import { CONCLUSIONES_IS_DEFAULT } from "./is/is-payload.js";

type ChildTipo = Extract<TipoInforme, "IS" | "AC">;
type ParentTipo = Extract<TipoInforme, "IE" | "IS">;
type ParentPayload = IePayload | IsPayload;
type ChildPayload = IsPayload | AcPayload;

/**
 * Builds the child report payload from the parent's local payload.
 *
 * @param parentPayload - The full payload of the parent (from IndexedDB or editor state).
 * @param parentTipo    - "IE" or "IS"
 * @param childTipo     - "IS" or "AC"
 * @returns             - A fully-initialized child payload ready for enqueueReport.
 */
export function buildChildPayload(
  parentPayload: ParentPayload,
  parentTipo: ParentTipo,
  childTipo: ChildTipo,
): ChildPayload {
  if (parentTipo === "IE" && childTipo === "IS") {
    return buildIeToIs(parentPayload as IePayload);
  }
  if (parentTipo === "IE" && childTipo === "AC") {
    return buildIeToAc(parentPayload as IePayload);
  }
  if (parentTipo === "IS" && childTipo === "AC") {
    return buildIsToAc(parentPayload as IsPayload);
  }
  // Should never reach here — TypeScript union exhaustion
  throw new Error(`Unsupported transition: ${parentTipo} → ${childTipo}`);
}

/** Fresh 7-slot default panel for a new IS — same titles as IE defaults, empty content. */
function freshIsPanelFotografico(): IeFoto[] {
  return PANEL_DEFAULT_TITULOS.map((titulo): IeFoto => ({ titulo, descripcion: "", url: "" }));
}

/**
 * Seed AC actividades from a parent panelFotografico — one actividad per photo slot
 * that carries a description. Mirrors the IS seeder (seedActividadesFromPanel), which
 * only runs in the editor when the técnico visits the IS actividades step. If that
 * step was never opened, IS.actividades stays [] in IDB even though the slot
 * descriptions exist in panelFotografico — so an AC built from it would inherit
 * nothing. Seeding from the panel here closes that gap for both IE→AC and IS→AC.
 */
function actividadesFromPanel(panel: IeFoto[]): IsActividad[] {
  return panel
    .filter((f) => f.descripcion.trim() !== "")
    .map((f) => ({ descripcion: f.descripcion }));
}

/**
 * IE → IS (REVISED — FIX 3):
 * Copies datosGenerales and equipos ONLY.
 * panelFotografico starts fresh (7 default slots, not inherited from IE).
 * actividades starts empty (auto-fills from the IS's own panel via InformeEditor's seeder).
 */
function buildIeToIs(parent: IePayload): IsPayload {
  return {
    // Deep-copy inherited sections so child mutations don't affect parent (NFR-2)
    datosGenerales: { ...parent.datosGenerales },
    equipos: parent.equipos.map((e) => ({ ...e })),
    // Fresh panel — IS fills its own photo evidence independently
    panelFotografico: freshIsPanelFotografico(),
    // Empty actividades — seeder in InformeEditor fills from IS's own panel on step visit
    actividades: [],
    // Conclusiones NOT inherited from IE — an IE has none. Seed the IS's 4 fixed
    // official conclusions (same as a brand-new IS); the técnico can edit/add.
    conclusionesRecomendaciones: [...CONCLUSIONES_IS_DEFAULT],
    tipoServicio: "otro",
    // No engrase seeded — new IS fills it
  };
}

/**
 * IE → AC:
 * Copies datosGenerales and equipos ONLY. An IE is an evaluation, not a service
 * record — its content does not map onto an AC's actividades/conclusiones, so the
 * técnico writes those fresh. actividades, conclusiones and firmas start empty.
 * (S-5.6)
 */
function buildIeToAc(parent: IePayload): AcPayload {
  return {
    datosGenerales: { ...parent.datosGenerales },
    equipos: parent.equipos.map((e) => ({ ...e })),
    // Seed actividades from the IE photo-slot descriptions so the AC arrives with the
    // evaluated points prefilled instead of blank. conclusiones start empty (an IE has none).
    actividades: actividadesFromPanel(parent.panelFotografico),
    conclusionesRecomendaciones: [],
    firmas: { tecnico: "" },
  };
}

/**
 * IS → AC:
 * Copies datosGenerales, equipos, actividades AND conclusionesRecomendaciones — the
 * AC inherits the full service record from its IS parent so the técnico signs off on
 * what was actually done. firmas starts empty.
 * (S-5.5)
 */
function buildIsToAc(parent: IsPayload): AcPayload {
  return {
    datosGenerales: { ...parent.datosGenerales },
    equipos: parent.equipos.map((e) => ({ ...e })),
    // IS→AC pulls the IS's own actividades verbatim (they already reflect the service done).
    actividades: parent.actividades.map((a) => ({ ...a })),
    conclusionesRecomendaciones: [...parent.conclusionesRecomendaciones],
    firmas: { tecnico: "" },
  };
}
